<script lang="ts" setup>
import { useCounter, useVModel } from '@vueuse/core'
// import Chip from 'primevue/chip'
import type { FormKitOptionsItem } from '@formkit/inputs'

type ModelValueType = Array<Pick<FormKitOptionsItem, 'label' | 'value'>>

const props = defineProps({
  modelValue: null,
  suggestions: null,
  loading: { type: Boolean }
})

const emit = defineEmits(["update:modelValue", "search"])

const containerRef = ref<HTMLDivElement>()
const suggestionContainerRef = ref<HTMLUListElement>()

const query = ref('')
const value = useVModel(props, 'modelValue', emit)

const activeIndex = useCounter(0)

const filteredSuggestions = computed<ModelValueType>(() =>
  props.suggestions.filter(
    ({ value }) => !props.modelValue.find(({ value: vValue }) => vValue === value),
  ),
)

function remove(index: number) {
  value.value = value.value.filter((_, a) => a !== index)
}

function select(index: number) {
  if (query.value && filteredSuggestions.value.at(index)) {
    value.value = [...value.value, filteredSuggestions.value.at(index)]
  }

  activeIndex.reset()
  query.value = ''
}

const inputChanged = useDebounceFn(function inputChanged(event: InputEvent) {
  emit('search', (event.target as HTMLInputElement).value)
}, 250)

function keyDown(event: KeyboardEvent) {
  switch (event.code) {
    case 'Backspace':
      if (!query.value) {
        value.value.pop()
      }
      break
    case 'Enter':
      select(activeIndex.count.value)
      event.preventDefault()
      break
    case 'ArrowUp':
      if (activeIndex.get() === 0) {
        activeIndex.set(filteredSuggestions.value.length)
      }
      activeIndex.dec()
      break
    case 'ArrowDown':
      if (activeIndex.get() === filteredSuggestions.value.length - 1) {
        activeIndex.set(0)
      } else activeIndex.inc()
      break
  }
}

onUpdated(() => {
  if (containerRef.value && suggestionContainerRef.value) {
    const { left, bottom, width } = containerRef.value?.getBoundingClientRect()
    suggestionContainerRef.value!.style.setProperty(
      'z-index',
      Number.MAX_SAFE_INTEGER.toString(),
    )
    suggestionContainerRef.value!.style.setProperty('top', bottom + 'px')
    suggestionContainerRef.value!.style.setProperty('left', left + 'px')
    suggestionContainerRef.value!.style.setProperty('width', width + 'px')
  }
})
</script>

<template>
  <div ref="containerRef" class="input group relative w-full">
    <div
      :class="[
        'flex flex-row flex-wrap items-center gap-2 px-6',
        { 'py-5': !value.length, 'py-4': value.length },
      ]"
    >
      
      <input
        v-model="query"
        type="text"
        class="min-w-[9rem] flex-1 border-none p-0 outline-none"
        v-bind="$attrs"
        :placeholder="$attrs.placeholder ?? 'Start typing for suggestion'"
        @keydown="keyDown"
        @input="inputChanged"
      />
    </div>
    <Teleport
      v-if="loading || (filteredSuggestions.length && query.length)"
      to="body"
    >
      <ul
        ref="suggestionContainerRef"
        class="scrollbar-hide fixed mt-1 grid max-h-56 gap-2 overflow-y-scroll rounded border bg-white p-2"
      >
        <progress-spinner v-if="loading" class="h-10 w-10" />
        <template v-else-if="query.length">
          <li
            v-for="(suggestion, index) in filteredSuggestions"
            :key="index"
            tabindex="0"
            role="option"
            :class="[
              'w-full rounded-lg p-3 font-medium',
              'cursor-pointer hover:bg-bunce-primary/10',
              {
                'bg-bunce-primary/10': activeIndex.count.value === index,
              },
            ]"
            @keydown.enter="select(index)"
            @click="select(index)"
            @mouseenter="activeIndex.set(index)"
            v-text="suggestion.label"
          />
        </template>
      </ul>
    </Teleport>
  </div>
</template>
